import {
  AddIcon,
  AiIcon,
  Alert,
  Button,
  ChatMessage,
  ChatPanel,
  Chip,
  EmptyState,
  HTMLDisplay,
  ListDetailLayout,
  ListPanelHeader,
  RefreshIcon,
  Skeleton,
  Stack,
  Typography,
} from '@all-manager/design-system-v2';
import FullPageLayout from '../../../../layout/FullPageLayout/FullPageLayout';
import ChatComposer from '../../components/ChatComposer/ChatComposer';
import ChatSources from '../../components/ChatSources/ChatSources';
import ConversationList from '../../components/ConversationList/ConversationList';
import { answerToHtml } from '../../utils/answerToHtml';
import { SUGGESTED_QUESTIONS, useAiChatPage } from './useAiChatPage';

const PENDING_SKELETON_WIDTH = 220;
const SETTINGS_SKELETON_HEIGHT = 240;
const MESSAGES_SKELETON_HEIGHT = 72;

export default function AiChatPage() {
  const hook = useAiChatPage();

  const newConversationButton = hook.conversationId || hook.hasConversation ? (
    <Button
      variant="contained"
      size="small"
      startIcon={<AddIcon />}
      onClick={hook.handleNewConversation}
      disabled={hook.isPending}
    >
      Nouvelle conversation
    </Button>
  ) : undefined;

  function renderWelcome() {
    return (
      <EmptyState
        title="Que voulez-vous savoir ?"
        subtitle="L'assistant cherche pour vous dans le calendrier, les sujets, les prestataires et le contenu des documents."
        icon={<AiIcon color="ai" />}
        action={(
          <Stack direction="row" spacing={1} wrap="wrap" justifyContent="center">
            {SUGGESTED_QUESTIONS.map((question) => (
              <Chip
                key={question}
                label={question}
                variant="outlined"
                onClick={() => hook.handleSubmit(question)}
              />
            ))}
          </Stack>
        )}
      />
    );
  }

  function renderConversation() {
    if (hook.isConversationMissing) {
      return (
        <Alert severity="info">
          Cette conversation n'existe plus : les échanges sont conservés 30 jours.
        </Alert>
      );
    }

    if (hook.isMessagesLoading) {
      return (
        <Stack spacing={2} aria-busy="true" aria-label="Chargement de la conversation">
          <Skeleton variant="rounded" height={MESSAGES_SKELETON_HEIGHT} />
          <Skeleton variant="rounded" height={MESSAGES_SKELETON_HEIGHT} />
        </Stack>
      );
    }

    if (!hook.hasConversation) {
      return renderWelcome();
    }

    return (
      <>
        {hook.messages.map((message) => (
          message.role === 'user' ? (
            <ChatMessage key={message.id} author="user">
              <Typography variant="body1">{message.text}</Typography>
            </ChatMessage>
          ) : (
            <ChatMessage
              key={message.id}
              author="assistant"
              footer={<ChatSources sources={message.sources ?? []} />}
            >
              <HTMLDisplay content={answerToHtml(message.text)} />
            </ChatMessage>
          )
        ))}

        {hook.pendingQuestion ? (
          <>
            <ChatMessage author="user">
              <Typography variant="body1">{hook.pendingQuestion}</Typography>
            </ChatMessage>
            <ChatMessage author="assistant">
              <Typography variant="body1">Je cherche dans les données de la copropriété…</Typography>
              <Skeleton variant="text" width={PENDING_SKELETON_WIDTH} />
            </ChatMessage>
          </>
        ) : null}

        {hook.failedQuestion ? (
          <ChatMessage
            author="user"
            footer={(
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="caption">Non envoyé</Typography>
                <Button
                  variant="text"
                  size="small"
                  color="default"
                  startIcon={<RefreshIcon />}
                  onClick={hook.handleRetry}
                  disabled={hook.isPending}
                >
                  Réessayer
                </Button>
              </Stack>
            )}
          >
            <Typography variant="body1">{hook.failedQuestion}</Typography>
          </ChatMessage>
        ) : null}

        {hook.errorMessage ? <Alert severity="error">{hook.errorMessage}</Alert> : null}
      </>
    );
  }

  function renderContent() {
    if (hook.isSettingsLoading) {
      return <Skeleton variant="rounded" height={SETTINGS_SKELETON_HEIGHT} />;
    }

    if (!hook.isAiEnabled) {
      return (
        <Alert severity="info">
          L'IA est désactivée pour cette copropriété. Un administrateur peut la réactiver dans Paramètres de l'espace › Intelligence artificielle.
        </Alert>
      );
    }

    return (
      <ListDetailLayout
        showDetail={hook.showDetail}
        list={(
          <ConversationList
            selectedConversationId={hook.conversationId}
            onSelectConversation={hook.handleSelectConversation}
            back={hook.isCompact ? { href: hook.conversationPath, onClick: hook.handleCloseHistory } : undefined}
          />
        )}
        detail={(
          <ChatPanel
            fill
            label="Conversation avec l'assistant"
            scrollKey={hook.scrollKey}
            header={(
              <ListPanelHeader
                title={hook.title}
                action={hook.isCompact ? (
                  <Button variant="outlined" size="small" onClick={hook.handleOpenHistory}>
                    {hook.historyButtonLabel}
                  </Button>
                ) : undefined}
              />
            )}
            composer={(
              <ChatComposer
                onSubmit={hook.handleSubmit}
                disabled={hook.isPending || hook.isConversationMissing}
              />
            )}
          >
            {renderConversation()}
          </ChatPanel>
        )}
      />
    );
  }

  return (
    <FullPageLayout
      htmlTitle="Assistant IA"
      title="Assistant IA"
      action={newConversationButton}
      fixedHeader
    >
      {renderContent()}
    </FullPageLayout>
  );
}
